import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { X, Check } from 'lucide-react';

interface HashtagModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onInsert: (tag: string) => void;
}

export function HashtagModal({ open, onOpenChange, onInsert }: HashtagModalProps) {
  const [newTag, setNewTag] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (open) {
      const stored = localStorage.getItem('savedTags');
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          const normalized = parsed.map((tag: string) => tag.replace(/^#/, ''));
          setTags(normalized);
        } catch {
          console.warn('Could not parse saved tags');
        }
      }
      // Reset selected tags when modal opens
      setSelectedTags(new Set());
    }
  }, [open]);

  const saveTagsToStorage = (newTags: string[]) => {
    setTags(newTags);
    localStorage.setItem('savedTags', JSON.stringify(newTags));
  };

  const handleAddTag = () => {
    const clean = newTag.trim().replace(/^#/, '');
    if (!clean || tags.includes(clean)) return;
    const updated = [...tags, clean];
    saveTagsToStorage(updated);
    setNewTag('');
  };

  const handleDeleteTag = (tagToDelete: string) => {
    const updated = tags.filter((tag) => tag !== tagToDelete);
    saveTagsToStorage(updated);
    // Remove from selected if it was selected
    setSelectedTags(prev => {
      const newSet = new Set(prev);
      newSet.delete(tagToDelete);
      return newSet;
    });
  };

  const handleToggleTag = (tag: string) => {
    setSelectedTags(prev => {
      const newSet = new Set(prev);
      if (newSet.has(tag)) {
        newSet.delete(tag);
      } else {
        newSet.add(tag);
      }
      return newSet;
    });
  };

  const handleInsertSelected = () => {
    selectedTags.forEach(tag => {
      const formatted = tag.startsWith('#') ? tag : `#${tag}`;
      onInsert(formatted);
    });
    setSelectedTags(new Set());
    onOpenChange(false);
  };

  const handleClose = () => {
    setSelectedTags(new Set());
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Manage Hashtags</DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-2">
          <Input
            placeholder="Enter a hashtag"
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddTag();
              }
            }}
          />
          <Button onClick={handleAddTag} variant="secondary">
            Save
          </Button>
        </div>

        <div className="flex flex-wrap gap-2 mt-4">
          {tags.length > 0 ? (
            tags.map((tag) => {
              const isSelected = selectedTags.has(tag);
              return (
                <div
                  key={tag}
                  className={`text-sm px-3 py-1 rounded-full flex items-center gap-2 cursor-pointer transition-all duration-200 ${
                    isSelected
                      ? 'bg-green-100 border-2 border-green-500 text-green-800 hover:bg-green-150'
                      : 'bg-muted hover:bg-muted/70 border-2 '
                  }`}
                  onClick={() => handleToggleTag(tag)}
                >
                  <span>#{tag}</span>
                  <div className="flex items-center gap-1">
                    {isSelected && (
                      <Check className="w-3 h-3 text-green-600" />
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteTag(tag);
                      }}
                      className="hover:text-red-500 transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              );
            })
          ) : (
            <p className="text-muted-foreground text-sm">No saved hashtags.</p>
          )}
        </div>

        {selectedTags.size > 0 && (
          <div className="mt-4 p-3 bg-muted rounded-lg border border-border">
            <p className="text-sm text-foreground font-medium mb-2">
              Selected tags ({selectedTags.size}):
            </p>
            <div className="flex flex-wrap gap-1">
              {Array.from(selectedTags).map((tag) => (
                <span key={tag} className="text-xs bg-muted-foreground/20 text-foreground px-2 py-1 rounded">
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        )}

        <DialogFooter className="flex justify-between">
          <Button variant="outline" onClick={handleClose}>
            Close
          </Button>
          <Button 
            onClick={handleInsertSelected} 
            disabled={selectedTags.size === 0}
            className="bg-green-600 hover:bg-green-700"
          >
            Insert Selected ({selectedTags.size})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}